import React, { useState, useRef, useEffect } from 'react';

const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder,
  className,
  onToggleEdit = null, // Optional prop for edit mode toggle
  isEditable = false, // Optional prop to control edit mode
  disabled = false,
  getOptionLabel = (option) => option // Default function to get label from option
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const [userHasTyped, setUserHasTyped] = useState(false);

  // Update searchTerm when value changes from parent, but only if user hasn't typed
  useEffect(() => {
    if (!userHasTyped && value) {
      setSearchTerm(typeof value === 'string' ? value : getOptionLabel(value));
    }
  }, [value, getOptionLabel, userHasTyped]);

  const filteredOptions = options.filter(option => {
    const label = getOptionLabel(option);
    return label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // Reset userHasTyped when clicking outside and no value is selected
        if (!value) {
          setUserHasTyped(false);
          setSearchTerm('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setUserHasTyped(true);
    setIsOpen(true);
    
    if (newValue === '') {
      onChange(null); // Clear the value in parent component
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative flex items-center">
        {isEditable ? (
          <input
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            className={className}
            placeholder={placeholder}
            disabled={disabled}
          />
        ) : (
          <input
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onClick={() => !disabled && setIsOpen(true)}
            onFocus={() => !disabled && setIsOpen(true)}
            placeholder={placeholder}
            className={className}
            disabled={disabled}
          />
        )}
        {onToggleEdit && (
          <button
            type="button"
            onClick={() => onToggleEdit(!isEditable)}
            className="absolute right-2 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        )}
      </div>
      
      {isOpen && !isEditable && !disabled && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              className="px-4 py-2 cursor-pointer hover:bg-gray-100"
              onClick={() => {
                onChange(option);
                setSearchTerm(getOptionLabel(option));
                setIsOpen(false);
                setUserHasTyped(false);
              }}
            >
              {getOptionLabel(option)}
            </div>
          ))}
          {filteredOptions.length === 0 && (
            <div className="px-4 py-2 text-gray-500">
              No options found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown; 